import { Trans, useLingui } from '@lingui/react/macro';
import { Typography } from '@remarkable/ark-web';
import type { ColumnDef } from '@tanstack/react-table';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import type { Integration } from 'src/api/endpoints/cloudApi.types';
import { IntegrationIssueType } from 'src/api/endpoints/cloudApi.types';
import {
  useDeleteIntegration,
  useGetIntegrationReauthURI,
  useHasSubscription,
  useIntegrationProviders,
  useIntegrations,
  useUpdateIntegration,
} from 'src/api/queries';
import { Button, Modal, NotificationBox, Spinner, Table } from 'src/components';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { IntegrationIcon } from 'src/components/IntegrationIcon';
import { Tooltip } from 'src/components/Tooltip';
import { formatDate } from 'src/utils/formatDate';

import { ArrowRight } from '@remarkable/ark-icons/ArrowRight';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { Link2 } from 'src/components/Link2';
import { URLS } from 'src/utils/urls/urls';
import { getProviderDisplayName } from '../utils/getProviderDisplayName';
import { getPrimaryIssue } from '../utils/integrationIssues';
import { EditIntegrationNamePopover } from './EditNamePopover';
import { getIssueCopy, IntegrationStatus } from './IntegrationStatus';
import { MobileIntegrationDetailsRow } from './MobileIntegrationDetailsRow';
import { ReAuthorizeIntegrationButton } from './ReAuthoriseIntegration';
import { RemoveIntegrationModal } from './RemoveIntegrationModal';

const columnHelper = createColumnHelper<Integration>();

export const IntegrationsList = () => {
  const { t, i18n } = useLingui();
  const hasSubscription = useHasSubscription();
  const integrations = useIntegrations();
  const providers = useIntegrationProviders();
  const getIntegrationReauthURI = useGetIntegrationReauthURI();
  const deleteIntegration = useDeleteIntegration();
  const updateIntegration = useUpdateIntegration();

  const columns = [
    columnHelper.accessor('name', {
      header: t`Integration`,
      cell: (info) => {
        return (
          <span className="flex flex-row items-center gap-8">
            <IntegrationIcon
              className="size-24"
              provider={info.row.original.provider}
              alt={info.row.original.provider}
            />
            <Typography variant="interface-md" leadingNone fontWeight="medium">
              {info.cell.getValue()}
            </Typography>
          </span>
        );
      },
    }),
    columnHelper.accessor(
      (integration) => getProviderDisplayName(integration.provider),
      {
        header: t`Service`,
      }
    ),
    columnHelper.accessor(
      (integration) =>
        formatDate(integration.added, {
          locale: i18n.locale,
        }),
      {
        header: t`Added`,
      }
    ),
    columnHelper.display({
      id: 'status',
      header: t`Status`,
      cell: (info) => {
        const issue = getPrimaryIssue(info.row.original);
        const status = <IntegrationStatus issues={info.row.original.issues} />;

        if (!issue) return status;

        return (
          <Tooltip side="bottom" title={getIssueCopy(issue)} asChild>
            <span className="cursor-help">{status}</span>
          </Tooltip>
        );
      },
    }),
    columnHelper.display({
      id: 'reauthorize',
      meta: {
        align: 'right',
        truncate: false,
      },
      cell: (info) => {
        const primaryIssue = getPrimaryIssue(info.row.original);
        if (
          primaryIssue?.type === IntegrationIssueType.PROVIDER_REVOKED_ACCESS
        ) {
          return (
            <ReAuthorizeIntegrationButton
              integration={info.row.original}
              getIntegrationReauthURIMutation={getIntegrationReauthURI}
              dataCy="renew-access-button-desktop"
            />
          );
        }
        if (primaryIssue?.type === IntegrationIssueType.NO_CONNECT) {
          return (
            <Link2
              to={URLS.STORE_CONNECT_CHECKOUT}
              componentLocation={`${ComponentLocations.INTEGRATIONS.ACTIVE_INTEGRATION_ITEM} ${info.row.original.provider}`}
              as="button"
              variant="secondary"
              size="small"
              className="text-14!"
            >
              <span className="text-center">
                <Trans>Get Connect</Trans>
              </span>
              <ArrowRight />
            </Link2>
          );
        }
      },
    }),
    columnHelper.display({
      id: 'menu',
      meta: {
        align: 'center',
        colWidth: 'w-[50px]',
        truncate: false,
      },
      cell: (info) => (
        <DropdownMenu
          trigger={
            <Button
              variant="tertiary-neutral"
              className="!rounded data-[state=open]:bg-interaction-tertiary-active"
              size="small"
              data-cy={`integration-option-button-${info.row.original.id}`}
            >
              <Trans>Edit</Trans>
            </Button>
          }
        >
          <Modal.Root>
            <RemoveIntegrationModal
              integrationId={info.row.original.id}
              integrationName={getProviderDisplayName(info.row.original.name)}
            />
            <Modal.Trigger asChild>
              <Button
                variant="tertiary-neutral"
                data-cy="remove-integration-button"
                size="medium"
              >
                <span>
                  <Trans>Remove</Trans>
                </span>
              </Button>
            </Modal.Trigger>
            <EditIntegrationNamePopover
              id={info.row.original.id}
              name={info.row.original.name}
            />
          </Modal.Root>
        </DropdownMenu>
      ),
    }),
  ];

  const table = useReactTable({
    data: integrations.data || [],
    columns: columns as ColumnDef<Integration>[],
    getRowId: ({ id }) => id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    renderFallbackValue: () => '-',
  });

  if (
    integrations.isPending ||
    providers.isPending ||
    hasSubscription.isPending
  )
    return <Spinner />;
  if (
    integrations.isError ||
    providers.isError ||
    hasSubscription.error ||
    deleteIntegration.isError ||
    updateIntegration.isError
  )
    return (
      <NotificationBox
        className="mt-24"
        error={[
          integrations.error,
          providers.error,
          hasSubscription.error,
          deleteIntegration.error,
          updateIntegration.error,
        ]}
        title={t`Something went wrong. Please try again later.`}
      />
    );

  if (integrations.data?.length === 0) return null;

  return (
    <>
      <Table
        table={table}
        data-cy="integrations-table"
        className="hidden lm:table"
      />
      <ul
        className="border-t border-gray-100 lm:hidden"
        data-cy="integrations-list"
      >
        {table.getRowModel().rows.map((row) => (
          <li
            data-cy={`integration-row-${row.id}`}
            key={row.id}
            className="border-b border-gray-100 py-12"
          >
            <div className="flex flex-col gap-8">
              <div className="flex min-w-0 items-center justify-between gap-8">
                <Typography
                  variant="body-sm"
                  className="truncate font-semibold"
                >
                  {row.original.name}
                </Typography>
                <DropdownMenu
                  trigger={
                    <Button
                      variant="tertiary-neutral"
                      className="!rounded data-[state=open]:bg-interaction-tertiary-active"
                      size="small"
                    >
                      <Trans>Edit</Trans>
                    </Button>
                  }
                >
                  <Modal.Root>
                    <RemoveIntegrationModal
                      integrationId={row.original.id}
                      integrationName={getProviderDisplayName(
                        row.original.name
                      )}
                    />
                    <Modal.Trigger asChild>
                      <Button
                        variant="tertiary-neutral"
                        data-cy="remove-integration-button"
                        size="medium"
                      >
                        <span>
                          <Trans>Remove</Trans>
                        </span>
                      </Button>
                    </Modal.Trigger>
                    <EditIntegrationNamePopover
                      id={row.original.id}
                      name={row.original.name}
                    />
                  </Modal.Root>
                </DropdownMenu>
              </div>
              <MobileIntegrationDetailsRow integration={row.original} />
              {getPrimaryIssue(row.original)?.type ===
                IntegrationIssueType.PROVIDER_REVOKED_ACCESS && (
                <ReAuthorizeIntegrationButton
                  dataCy="renew-access-button-mobile"
                  integration={row.original}
                  getIntegrationReauthURIMutation={getIntegrationReauthURI}
                />
              )}
              {getPrimaryIssue(row.original)?.type ===
                IntegrationIssueType.NO_CONNECT && (
                <Link2
                  to={URLS.STORE_CONNECT_CHECKOUT}
                  componentLocation={`${ComponentLocations.INTEGRATIONS.ACTIVE_INTEGRATION_ITEM} ${row.original.provider}`}
                  as="button"
                  variant="secondary"
                  size="small"
                  className="text-14!"
                >
                  <span className="text-center">
                    <Trans>Get Connect</Trans>
                  </span>
                  <ArrowRight />
                </Link2>
              )}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
};
